import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { getCtoAudit } from '../utils/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import {
  TITLE_OPTIONS, CONTACT_METHOD_OPTIONS, SCORE_TOOLTIP, SCORE_GUIDE,
  getAction, ACTION_COLORS, getSuggestedScore,
} from '../utils/ctoShared';

const CUSTOM = '__custom__';

const inp = {
  background: 'var(--bg-card)', border: '1px solid var(--border-med)',
  borderRadius: 'var(--radius-sm)', color: 'var(--text)',
  padding: '7px 10px', fontSize: 13,
  fontFamily: 'DM Sans, sans-serif', width: '100%', outline: 'none',
};

const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.07em', marginBottom: 4,
};

function Field({ label, children }) {
  return <div style={{ marginBottom: 14 }}><label style={lbl}>{label}</label>{children}</div>;
}

function Section({ title, children }) {
  return (
    <div style={{
      background: 'rgba(46,162,235,0.06)',
      border: '1px solid var(--primary-border)',
      borderRadius: 'var(--radius)',
      padding: '14px 16px', marginBottom: 18,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ── History ──────────────────────────────────────────────────────────────────
// Field order and labels for the History tab (also the order rows are shown in).
const FIELD_LABELS = [
  ['candidate_name',      'Candidate Name'],
  ['linkedin_url',        'LinkedIn URL'],
  ['current_title',       'Current Title'],
  ['years_experience',    'Years Experience'],
  ['company_type',        'Company Type'],
  ['relevant_tech',       'Relevant Tech'],
  ['backend_focus',       'Backend Focus'],
  ['system_architecture', 'System / Architecture'],
  ['leadership_signals',  'Leadership Signals'],
  ['red_flags',           'Red Flags'],
  ['score',               'Score'],
  ['notes',               'Notes'],
  ['contacted',           'Contacted'],
  ['contact_method',      'Contact Method'],
  ['contact_date',        'Contact Date'],
  ['response',            'Response'],
  ['response_date',       'Response Date'],
  ['next_step',           'Next Step'],
  ['closed',              'Closed'],
];
const FIELD_ORDER = Object.fromEntries(FIELD_LABELS.map(([k], i) => [k, i]));
const FIELD_NAME  = Object.fromEntries(FIELD_LABELS);
const BOOL_FIELDS = new Set(['backend_focus', 'system_architecture', 'contacted', 'closed']);
const DATE_FIELDS = new Set(['contact_date', 'response_date']);
const MAX_VALUE_LEN = 160;

function fmtStamp(d) {
  if (!d) return '—';
  try { return format(typeof d === 'string' ? parseISO(d) : d, 'MMM d, yyyy h:mm a'); }
  catch { return String(d); }
}

function fmtValue(field, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (BOOL_FIELDS.has(field)) return v === 'true' ? 'Yes' : 'No';
  if (DATE_FIELDS.has(field)) {
    try { return format(parseISO(v), 'MMM d, yyyy'); } catch { return v; }
  }
  return v.length > MAX_VALUE_LEN ? `${v.slice(0, MAX_VALUE_LEN)}…` : v;
}

const pill = (bg, color) => ({ background: bg, color, borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600 });

function HistoryTab({ candidateId }) {
  const [log,     setLog]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getCtoAudit(candidateId)
      .then(data => setLog(data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>Loading history…</div>;

  if (error) return (
    <div style={{ padding: 20, fontSize: 13 }}>
      <div style={{ color: 'var(--danger)', marginBottom: 10 }}>⚠ Couldn't load history.</div>
      <button onClick={load} style={{
        background: 'var(--bg-card)', border: '1px solid var(--border-med)',
        borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
        fontFamily: 'DM Sans, sans-serif', fontSize: 13, padding: '7px 14px', cursor: 'pointer',
      }}>Retry</button>
    </div>
  );

  if (!log.length) return (
    <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>
      No history yet. Changes are recorded on every save.
    </div>
  );

  // Rows from one save share a batch_id; the API returns them newest first.
  const groups = [];
  const byBatch = new Map();
  for (const e of log) {
    let g = byBatch.get(e.batch_id);
    if (!g) {
      g = { id: e.batch_id, at: e.changed_at, by: e.changed_by, type: e.change_type, rows: [] };
      byBatch.set(e.batch_id, g);
      groups.push(g);
    }
    g.rows.push(e);
  }
  groups.forEach(g => g.rows.sort((a, b) => (FIELD_ORDER[a.field_name] ?? 99) - (FIELD_ORDER[b.field_name] ?? 99)));

  const typeLabel = { create: 'Created', update: 'Updated', delete: 'Deleted' };

  return (
    <div style={{ padding: '0 0 20px' }}>
      {groups.map((g, i) => (
        <div key={g.id} style={{ padding: '12px 0', borderBottom: i < groups.length - 1 ? '1px solid var(--border)' : 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{fmtStamp(g.at)}</span>
            <span style={pill('var(--primary-dim)', 'var(--primary)')}>{g.by || 'Unknown'}</span>
            <span style={pill('rgba(167,139,250,0.1)', '#A78BFA')}>{typeLabel[g.type] || g.type}</span>
          </div>
          {g.type === 'update' ? (
            <div style={{ fontSize: 12 }}>
              {g.rows.map(r => (
                <div key={r.id} style={{ marginBottom: 4, wordBreak: 'break-word' }}>
                  <span style={{ color: 'var(--text-dim)' }}>{FIELD_NAME[r.field_name] || r.field_name}: </span>
                  <span style={{ color: 'var(--text-muted)' }}>{fmtValue(r.field_name, r.old_value)}</span>
                  <span style={{ color: 'var(--text-dim)' }}> → </span>
                  <span style={{ color: 'var(--text)' }}>{fmtValue(r.field_name, r.new_value)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text)', wordBreak: 'break-word' }}>
              {g.rows[0].candidate_name || 'Unnamed candidate'}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Form helpers ─────────────────────────────────────────────────────────────
const dayOf = (v) => (v ? String(v).split('T')[0] : '');

function buildForm(c) {
  // Same 1–5 clamp the old inline score editor applied.
  const score = c.score ? Math.min(5, Math.max(1, parseInt(c.score, 10) || 0)) : 0;
  return {
    candidate_name:      c.candidate_name      || '',
    linkedin_url:        c.linkedin_url        || '',
    current_title:       c.current_title       || '',
    years_experience:    c.years_experience === null || c.years_experience === undefined ? '' : String(c.years_experience),
    company_type:        c.company_type        || '',
    relevant_tech:       c.relevant_tech       || '',
    backend_focus:       !!c.backend_focus,
    system_architecture: !!c.system_architecture,
    leadership_signals:  c.leadership_signals  || '',
    red_flags:           c.red_flags           || '',
    score:               score ? String(score) : '',
    notes:               c.notes               || '',
    contact_method:      c.contact_method      || '',
    contact_date:        dayOf(c.contact_date),
    response:            c.response            || '',
    response_date:       dayOf(c.response_date),
    next_step:           c.next_step           || '',
    closed:              !!c.closed,
  };
}

const orNull = (v) => (v === '' ? null : v);

function toPayload(form) {
  const years = parseInt(form.years_experience, 10);
  return {
    candidate_name:      form.candidate_name.trim(),
    linkedin_url:        orNull(form.linkedin_url),
    current_title:       orNull(form.current_title),
    years_experience:    Number.isNaN(years) ? null : years,
    company_type:        orNull(form.company_type),
    relevant_tech:       orNull(form.relevant_tech),
    backend_focus:       form.backend_focus,
    system_architecture: form.system_architecture,
    leadership_signals:  orNull(form.leadership_signals),
    red_flags:           orNull(form.red_flags),
    score:               form.score === '' ? null : parseInt(form.score, 10),
    notes:               orNull(form.notes),
    contact_method:      orNull(form.contact_method),
    contact_date:        orNull(form.contact_date),
    response:            orNull(form.response),
    response_date:       orNull(form.response_date),
    next_step:           orNull(form.next_step),
    closed:              form.closed,
  };
}

const isDirty = (a, b) => Object.keys(a).some(k => a[k] !== b[k]);

// ── Drawer ───────────────────────────────────────────────────────────────────
// Mount with key={candidate.id}: the form is initialised once from `candidate`.
export default function CTOEditDrawer({ candidate, onSave, onDelete, onClose }) {
  useBodyScrollLock();
  const [initial]  = useState(() => buildForm(candidate));
  const [form,     setForm]     = useState(initial);
  const [tab,      setTab]      = useState('edit');
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState('');
  const [titleCustom,  setTitleCustom]  = useState(() => !!initial.current_title && !TITLE_OPTIONS.includes(initial.current_title));
  const [methodCustom, setMethodCustom] = useState(() => !!initial.contact_method && !CONTACT_METHOD_OPTIONS.includes(initial.contact_method));

  // The conflict check compares against the version this form was opened from, even
  // if the list behind the drawer refreshes meanwhile.
  const baseUpdatedAt = useRef(candidate.last_updated_at || null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const dirty = isDirty(form, initial);

  const requestClose = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    onClose();
  };

  // Escape closes the drawer, except while a <select> has focus (Escape dismisses its list).
  const closeRef = useRef(requestClose);
  closeRef.current = requestClose;
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (e.target && e.target.tagName === 'SELECT') return;
      closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const handleSave = async () => {
    setError('');
    if (!form.candidate_name.trim()) {
      setError('Candidate Name is required to save.');
      return;
    }
    setSaving(true);
    try {
      await onSave(candidate.id, {
        ...candidate,                    // keep non-UI columns (e.g. contacted): PUT replaces the whole row
        ...toPayload(form),
        client_last_updated_at: baseUpdatedAt.current,
      });
    } catch (e) {
      setError(e.response?.data?.message || e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${candidate.candidate_name || 'this candidate'}"? This cannot be undone.`)) return;
    setDeleting(true);
    try { await onDelete(candidate.id); }
    catch { /* the parent already showed an error toast; keep the drawer open */ }
    finally { setDeleting(false); }
  };

  const onTitleSelect = (v) => {
    if (v === CUSTOM) {
      setTitleCustom(true);
      if (TITLE_OPTIONS.includes(form.current_title)) set('current_title', '');
    } else {
      setTitleCustom(false);
      set('current_title', v);
    }
  };

  const onMethodSelect = (v) => {
    if (v === CUSTOM) {
      setMethodCustom(true);
      if (CONTACT_METHOD_OPTIONS.includes(form.contact_method)) set('contact_method', '');
    } else {
      setMethodCustom(false);
      set('contact_method', v);
    }
  };

  const scoreNum   = form.score === '' ? null : Number(form.score);
  const action     = getAction(scoreNum);
  const suggested  = scoreNum ? null : getSuggestedScore({
    years_experience: parseInt(form.years_experience, 10),
    backend_focus: form.backend_focus,
    system_architecture: form.system_architecture,
  });

  const checkRow = (key, text) => (
    <label className="check-row" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <input
        type="checkbox" checked={form[key]}
        onChange={e => set(key, e.target.checked)}
        style={{ width: 15, height: 15, accentColor: 'var(--primary)' }}
      />
      <span style={{ fontSize: 13, color: 'var(--text)' }}>{text}</span>
    </label>
  );

  const textarea = (key, rows = 3) => (
    <textarea className="di" value={form[key]} onChange={e => set(key, e.target.value)} rows={rows} style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }} />
  );

  return (
    <>
      <div onClick={requestClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(2px)', zIndex: 200,
      }} />

      <div className="drawer-panel" role="dialog" aria-modal="true" aria-label="Edit CTO candidate" style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 520, maxWidth: '96vw',
        background: 'var(--bg-card2)',
        borderLeft: '1px solid var(--border-med)',
        zIndex: 201, display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
        animation: 'slideIn 0.2s ease',
      }}>
        <style>{`
          @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
          .di:focus { border-color: var(--primary) !important; }
        `}</style>

        {/* Header */}
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: 15, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {candidate.candidate_name || 'Unnamed candidate'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              CTO Candidate{candidate.current_title ? ` · ${candidate.current_title}` : ''}
            </div>
          </div>
          <button onClick={requestClose} aria-label="Close" style={{
            background: 'none', border: 'none',
            color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer',
          }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {['edit', 'history'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              flex: 1, background: 'none', border: 'none',
              borderBottom: tab === t ? '2px solid var(--primary)' : '2px solid transparent',
              color: tab === t ? 'var(--primary)' : 'var(--text-muted)',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: tab === t ? 600 : 400,
              fontSize: 13, padding: '10px', cursor: 'pointer',
              marginBottom: -1, textTransform: 'capitalize',
            }}>
              {t === 'edit' ? '✏️ Edit' : '📋 History'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="drawer-body" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {tab === 'history' ? (
            <HistoryTab candidateId={candidate.id} />
          ) : (
            <>
              {/* Candidate */}
              <Section title="◈ Candidate">
                <Field label="Candidate Name">
                  <input className="di" type="text" value={form.candidate_name} onChange={e => set('candidate_name', e.target.value)} style={inp} />
                </Field>
                <Field label="LinkedIn URL">
                  <input className="di" type="text" inputMode="url" autoCapitalize="none" placeholder="https://linkedin.com/in/…" value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} style={inp} />
                </Field>
                <Field label="Current Title">
                  <select className="di" value={titleCustom ? CUSTOM : form.current_title} onChange={e => onTitleSelect(e.target.value)} style={inp}>
                    <option value="">— none —</option>
                    {TITLE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    <option value={CUSTOM}>Custom / Type manually</option>
                  </select>
                  {titleCustom && (
                    <input className="di" type="text" placeholder="Type a title…" value={form.current_title} onChange={e => set('current_title', e.target.value)} style={{ ...inp, marginTop: 6 }} />
                  )}
                </Field>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Years Experience (7+ preferred)">
                    <input className="di" type="number" min="0" inputMode="numeric" value={form.years_experience} onChange={e => set('years_experience', e.target.value)} style={inp} />
                  </Field>
                  <Field label="Company Type">
                    <input className="di" type="text" value={form.company_type} onChange={e => set('company_type', e.target.value)} style={inp} />
                  </Field>
                </div>
                <Field label="Relevant Tech">{textarea('relevant_tech', 2)}</Field>
              </Section>

              {/* Evaluation */}
              <Section title="◈ Evaluation">
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Backend Focus">{checkRow('backend_focus', 'Backend focus')}</Field>
                  <Field label="System / Architecture Experience">{checkRow('system_architecture', 'Has system / architecture experience')}</Field>
                </div>
                <Field label="Leadership Signals">{textarea('leadership_signals')}</Field>
                <Field label="Red Flags">{textarea('red_flags')}</Field>
                <Field label="Score (1–5)">
                  <select className="di" value={form.score} onChange={e => set('score', e.target.value)} title={SCORE_TOOLTIP} style={inp}>
                    <option value="">— none —</option>
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={String(n)}>{n}</option>)}
                  </select>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 6, lineHeight: 1.5 }}>{SCORE_GUIDE}</div>
                  {suggested && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Suggested: {suggested}</div>
                  )}
                  {action && (
                    <div style={{ fontSize: 12, marginTop: 6 }}>
                      <span style={{ color: 'var(--text-dim)' }}>Action: </span>
                      <span style={{ color: ACTION_COLORS[action], fontWeight: 600 }}>{action}</span>
                    </div>
                  )}
                </Field>
                <Field label="Notes">{textarea('notes', 4)}</Field>
              </Section>

              {/* Outreach */}
              <Section title="◈ Outreach">
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 14px' }}>
                  <Field label="Contact Method">
                    <select className="di" value={methodCustom ? CUSTOM : form.contact_method} onChange={e => onMethodSelect(e.target.value)} style={inp}>
                      <option value="">— none —</option>
                      {CONTACT_METHOD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      <option value={CUSTOM}>Custom / Type manually</option>
                    </select>
                    {methodCustom && (
                      <input className="di" type="text" placeholder="Type method…" value={form.contact_method} onChange={e => set('contact_method', e.target.value)} style={{ ...inp, marginTop: 6 }} />
                    )}
                  </Field>
                  <Field label="Contact Date">
                    <input className="di" type="date" value={form.contact_date} onChange={e => set('contact_date', e.target.value)} style={inp} />
                  </Field>
                </div>
                <Field label="Response">{textarea('response')}</Field>
                <Field label="Response Date">
                  <input className="di" type="date" value={form.response_date} onChange={e => set('response_date', e.target.value)} style={inp} />
                </Field>
                <Field label="Next Step">{textarea('next_step')}</Field>
                <Field label="Closed">{checkRow('closed', 'Closed (Y/N)')}</Field>
              </Section>

              <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.8, marginTop: 8 }}>
                <div>Created: {candidate.created_at ? new Date(candidate.created_at).toLocaleString() : '—'} by {candidate.created_by || '—'}</div>
                <div>Last updated: {candidate.last_updated_at ? new Date(candidate.last_updated_at).toLocaleString() : '—'} by {candidate.last_updated_by || '—'}</div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {tab === 'edit' && (
          <div className="drawer-footer" style={{
            padding: '14px 20px', borderTop: '1px solid var(--border)',
            flexShrink: 0, background: 'var(--bg-card)',
          }}>
            {error && (
              <div style={{
                background: 'var(--danger-bg)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                fontSize: 12, padding: '7px 12px', marginBottom: 10,
              }}>
                ⚠ {error}
              </div>
            )}
            <div className="drawer-actions" style={{ display: 'flex', gap: 8 }}>
              <button className="drawer-save" onClick={handleSave} disabled={saving} style={{
                flex: 1,
                background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))',
                border: 'none', borderRadius: 'var(--radius-sm)',
                color: '#fff', fontFamily: 'DM Sans, sans-serif',
                fontWeight: 600, fontSize: 13, padding: '9px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1,
              }}>
                {saving ? 'Saving…' : '✓ Save'}
              </button>
              <button className="drawer-delete" onClick={handleDelete} disabled={deleting} style={{
                background: 'var(--danger-bg)',
                border: '1px solid rgba(248,113,113,0.2)',
                borderRadius: 'var(--radius-sm)', color: 'var(--danger)',
                fontFamily: 'DM Sans, sans-serif', fontWeight: 500,
                fontSize: 13, padding: '9px 16px', cursor: 'pointer',
              }}>
                {deleting ? '…' : 'Delete'}
              </button>
              <button className="drawer-cancel" onClick={requestClose} style={{
                background: 'var(--bg-card2)',
                border: '1px solid var(--border-med)',
                borderRadius: 'var(--radius-sm)', color: 'var(--text-muted)',
                fontFamily: 'DM Sans, sans-serif', fontSize: 13,
                padding: '9px 14px', cursor: 'pointer',
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
